/**
 * MCP settings API service — สวิตช์ระดับ environment ของ remote MCP (`/api/mcp/v1`)
 *
 * 4 คีย์นี้อยู่ในตาราง `cus.SystemConfig` หมวด `MCP` (ไม่ใช่ `sy.Dbsettings` เหมือนหน้า Core
 * Configurations) และ `WebAPI/Mcp/McpGate.cs` อ่านใหม่ทุก request โดยตั้งใจไม่ cache
 * ⇒ กดบันทึกแล้วมีผลทันที ไม่ต้อง restart backend · ฝั่งเครื่องมือ AI ต้อง reconnect เองถึงจะเห็นรายการ tool ใหม่
 *
 * ── 🔴 "" กับ "[]" คนละความหมาย (ทั้ง MCP_ENABLED_TOOLS และ MCP_PROTOCOL_VERSIONS)
 * ว่าง = "ไม่จำกัด" · `[]` = "ระบุแล้วว่าไม่มีตัวไหนเลย" — การ normalize ว่างให้เป็น `[]`
 * จะปิด MCP ทั้งใบเงียบ ๆ ⇒ ส่งค่าที่ผู้ใช้พิมพ์ไปตรง ๆ ห้ามเติมให้เอง
 *
 * ── สิทธิ์ (ต่างจากส่วนโทเคนในหน้าเดียวกัน)
 * `APIAuthen` เปิด `api/v1/SystemConfig/*` ไว้ให้ **role 0 (super admin) เท่านั้น** ส่วน
 * `api/v1/McpToken/*` เปิดถึง role 1 ⇒ admin ที่ไม่ใช่ role 0 จะออกโทเคนได้แต่แก้สวิตช์ไม่ได้
 * หน้าจอต้องบอกเรื่องนี้เป็นข้อความ ไม่ใช่ปล่อย 401 ดิบ ๆ ให้คนงง
 *
 * โครง axios / base-URL / cookie-auth / unwrap คัดลอกมาจาก `services/mcpTokenApi.js` ตรง ๆ
 * (house style ของตระกูลนี้) — ต่างกันแค่ `ROOT` กับรายชื่อเมธอด
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/** แกะ ResponseModel `{ message, status, data }` → `data` · throw เมื่อ envelope บอกว่าไม่สำเร็จ */
function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && ('status' in body || 'Status' in body)) {
    const status = 'status' in body ? body.status : body.Status;
    const data = 'data' in body ? body.data : body.Data;
    if (status === API_STATUS_SUCCESS) {
      return data;
    }
    const err = new Error(body.message || body.Message || 'Request failed');
    err.status = status;
    err.data = data;
    throw err;
  }
  return body;
}

/** แปลง error ของ axios ให้เป็น Error ที่พก message + HTTP status ของ API มาด้วย */
function toError(error) {
  const body = error && error.response && error.response.data;
  const message =
    (body && (body.message || body.Message)) ||
    (error && error.message) ||
    'Request failed';
  const err = new Error(message);
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  err.data = body && (body.data ?? body.Data);
  return err;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}

async function put(url, payload) {
  try {
    return unwrap(await http.put(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}

const ROOT = 'api/v1/SystemConfig/configs';

/** หมวดของแถวใน `cus.SystemConfig` ที่ seed ไว้ที่ `docs/sql/20260904/McpSystemConfig.sql` */
export const MCP_CATEGORY = 'MCP';

/** คีย์ทั้ง 4 ที่ McpGate อ่าน — ชื่อต้องตรงกับ `WebAPI/Mcp/McpGate.cs` (McpConfigKeys) เป๊ะ */
export const MCP_CONFIG_KEYS = {
  enabled: 'MCP_ENABLED',
  allowWrite: 'MCP_ALLOW_WRITE_TOOLS',
  enabledTools: 'MCP_ENABLED_TOOLS',
  protocolVersions: 'MCP_PROTOCOL_VERSIONS',
};

export const mcpSettingsApi = {
  /**
   * แถว config ทั้งหมวด MCP
   *
   * @returns {Promise<Array>} [{ configKey, configValue, configType, displayName, description, ... }]
   */
  list() {
    return get(`${ROOT}/category/${MCP_CATEGORY}`);
  },

  /**
   * เขียนค่าใหม่ให้คีย์เดียว (backend อัปเดตเฉพาะ ConfigValue + คอลัมน์ audit)
   *
   * @param {string} configKey คีย์ใน `MCP_CONFIG_KEYS`
   * @param {string} configValue ค่าดิบที่จะเก็บ — `'true'`/`'false'` สำหรับ Boolean และ JSON array
   *   (หรือสตริงว่าง = ไม่จำกัด) สำหรับ JSON · ห้ามแปลงค่าว่างเป็น `[]`
   */
  update(configKey, configValue) {
    return put(`${ROOT}/${configKey}`, { configValue });
  },
};

export default mcpSettingsApi;
